import { Box, Card, CardContent, Container, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from '../../api';
import AppBar from '../../app-bar/AppBar';
import ksimConsole from '../../assets/ksim-console.png';
import { useCheckRole } from '../../auth';
import Avavar from '../../avatar/Avatar';
import BackLink from '../../back-link/BackLink';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import TypeChip from '../../chip/TypeChip';
import Fallback from '../../fallback/Fallback';
import { apiPaths, queryClient } from '../../queryClient';
import { useSignalREffect } from '../../signalr/SignalRContext';
import SimulationSummaryCard from '../../simulation-summary-card/SimulationSummaryCard';
import SimulationSkeleton from '../../skeletons/SimulationSkeleton';
import { SimulationEventMessage } from '../../types';
import { useDesktop } from '../../useDesktop';
import { useActiveCompany } from '../../utils/useCompany';

const ExerciseStudent = () => {
  useCheckRole(['Instructor', 'Student']);

  const { exerciseId, userId } = useParams();
  const activeCompany = useActiveCompany();
  const { user, userError, userLoading } = useUser(exerciseId, userId);

  const isDesktop = useDesktop();
  const Wrapper = props => (isDesktop ? <Card {...props} /> : <Box {...props} />);
  const InnerWrapper = props => (isDesktop ? <CardContent {...props} /> : <Box {...props} />);

  const updateSimulationsList = () => {
    queryClient.fetchQuery({
      queryKey: [
        {
          path: apiPaths.teach.user(activeCompany?.companyId, exerciseId, userId),
        },
      ],
    });
  };

  useSignalREffect(SimulationEventMessage.ParticipantSimulationStarted, updateSimulationsList, [activeCompany]);
  useSignalREffect(SimulationEventMessage.ParticipantSimulationCompleted, updateSimulationsList, [activeCompany]);

  if (userLoading) {
    return (
      <SimulationSkeleton
        breadcrumbs={[
          { label: 'Home', to: '/teach' },
          { to: '..', label: <Skeleton width={160} /> },
          { label: <Skeleton width={120} /> },
        ]}
      />
    );
  }

  if (userError || !user) {
    throw userError;
  }

  const { avatar, exercise, name, simulations, status } = user;

  return (
    <>
      <AppBar title={name} />
      <Breadcrumbs
        items={[{ label: 'Home', to: '/teach' }, { to: '..', label: exercise.name }, { label: name }]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />

      <Container>
        <BackLink label="Back to exercise" sx={{ display: { xs: 'inline-block', sm: 'none' }, my: 2 }} to=".." />

        <Wrapper sx={{ backgroundColor: theme => theme.palette.background.default, mt: 4, mb: 3 }}>
          <InnerWrapper
            sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4, alignItems: 'center' }}
          >
            <Avavar src={avatar} sx={{ borderRadius: 4, height: 160, width: 160 }} />

            <Stack gap={2} width="100%">
              <Box display="flex" justifyContent="space-between">
                <Typography component="h1" variant="h3">
                  {name}
                </Typography>
                <TypeChip type={status} sx={{ textTransform: 'none' }} />
              </Box>
              <Typography color="primary" variant="h6">
                {exercise.name}
              </Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>{exercise.title}</Typography>
            </Stack>
          </InnerWrapper>
        </Wrapper>

        {simulations.length ? (
          <Stack gap={2}>
            {simulations.map(simulation => (
              <SimulationSummaryCard key={simulation.id} to={`simulations/${simulation.id}`} {...simulation} />
            ))}
          </Stack>
        ) : (
          <Fallback imgSrc={ksimConsole} message="No simulations have been run yet" />
        )}
      </Container>
    </>
  );
};
export default ExerciseStudent;
